import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import EmailTemplateManager from '../components/EmailTemplateManager';

const {
  FiUsers, FiBriefcase, FiAlertTriangle, FiFileText, FiTrendingUp, FiSettings,
  FiPlus, FiFilter, FiSearch, FiArrowRight, FiCheckCircle, FiClock,
  FiActivity, FiPieChart, FiMessageSquare, FiCalendar, FiMail,
  FiBarChart2, FiAward, FiShield, FiGlobe, FiLayout, FiDollarSign,
  FiTag, FiPackage, FiShare2, FiHeadphones, FiBook, FiList, FiBookOpen
} = FiIcons;

const SuperAdminDashboard = () => {
  const { user, logout } = useAuth();
  const { partners = [], utredere = [], loading } = useData();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!loading && (!user || user.role !== 'superadmin')) {
      navigate('/superadmin/login');
    }
  }, [user, loading, navigate]);

  if (loading) return <div className="p-8 text-white text-center">Initialiserer systemet...</div>;

  const pendingUtredere = (utredere || []).filter(u => u.status === 'pending_review').length;
  const courseLeadsCount = (utredere || []).filter(u => u.qualification_course_interest).length;
  const pendingPartners = (partners || []).filter(p => !p.active).length;

  const shortcuts = [
    { title: 'Rapportering', icon: FiBarChart2, link: '/superadmin/reporting', color: 'text-orange-400', bg: 'bg-orange-400/10' },
    { title: 'Land & Avdelinger', icon: FiGlobe, link: '/superadmin/corporate', color: 'text-blue-400', bg: 'bg-blue-400/10' },
    { title: 'Bedrifter', icon: FiLayout, link: '/superadmin/companies', color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { title: 'Utredere', icon: FiAward, link: '/superadmin/utredere', color: 'text-purple-400', bg: 'bg-purple-400/10', badge: pendingUtredere },
    { title: 'Kursleads', icon: FiBookOpen, link: '/superadmin/course-leads', color: 'text-amber-400', bg: 'bg-amber-400/10', badge: courseLeadsCount },
    { title: 'Kunder (Brukere)', icon: FiUsers, link: '/superadmin/users', color: 'text-green-400', bg: 'bg-green-400/10' },
    { title: 'Interne Ansatte', icon: FiShield, link: '/superadmin/internal-staff', color: 'text-indigo-400', bg: 'bg-indigo-400/10' },
    { title: 'Fakturering', icon: FiDollarSign, link: '/superadmin/invoices', color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
    { title: 'Meldinger (Chat)', icon: FiMessageSquare, link: '/superadmin/messages', color: 'text-pink-400', bg: 'bg-pink-400/10' },
    { title: 'Booking / Demoer', icon: FiCalendar, link: '/superadmin/bookings', color: 'text-cyan-400', bg: 'bg-cyan-400/10' },
    { title: 'Systeminnstillinger', icon: FiSettings, link: '/superadmin/system-settings', color: 'text-slate-400', bg: 'bg-slate-400/10' },
    { title: 'Priser og Rabatter', icon: FiTag, link: '/superadmin/pricing', color: 'text-violet-400', bg: 'bg-violet-400/10' },
    { title: 'Eksterne Tjenester', icon: FiPackage, link: '/superadmin/external-services', color: 'text-indigo-400', bg: 'bg-indigo-400/10' },
    { title: 'Partnere', icon: FiShare2, link: '/superadmin/partners', color: 'text-teal-400', bg: 'bg-teal-400/10', badge: pendingPartners },
    { title: 'Salg (Selgere)', icon: FiTrendingUp, link: '/superadmin/sales', color: 'text-green-500', bg: 'bg-green-500/10' },
    { title: 'Support Saker', icon: FiHeadphones, link: '/superadmin/support-tickets', color: 'text-red-400', bg: 'bg-red-400/10' },
    { title: 'Veiledere', icon: FiBook, link: '/superadmin/guides', color: 'text-teal-300', bg: 'bg-teal-300/10' },
    { title: 'Kategorier', icon: FiList, link: '/superadmin/categories', color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
  ];

  return (
    <div className="min-h-screen bg-[#0F172A] text-slate-200 font-sans">
      <nav className="bg-slate-900/50 backdrop-blur-md border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <SafeIcon icon={FiShield} className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-white tracking-tight">SuperAdmin</span>
            </div>
            <div className="flex items-center space-x-4">
              <button onClick={logout} className="bg-slate-800 hover:bg-red-900/30 text-slate-300 hover:text-red-400 p-2 rounded-xl border border-slate-700 transition-all">
                <SafeIcon icon={FiArrowRight} className="w-5 h-5 rotate-180" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex space-x-8 border-b border-slate-800 mb-8">
          <button onClick={() => setActiveTab('overview')} className={`pb-4 text-sm font-bold transition-all border-b-2 ${activeTab === 'overview' ? 'border-blue-500 text-white' : 'border-transparent text-slate-400 hover:text-slate-200'}`}>Systemoversikt</button>
          <button onClick={() => setActiveTab('email')} className={`pb-4 text-sm font-bold transition-all border-b-2 ${activeTab === 'email' ? 'border-blue-500 text-white' : 'border-transparent text-slate-400 hover:text-slate-200'}`}>E-post Maler</button>
        </div>

        {activeTab === 'overview' && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4 mb-10">
            {shortcuts.map((action, i) => (
              <Link key={i} to={action.link} className="flex flex-col items-center justify-center p-4 bg-slate-900/50 border border-slate-800 rounded-2xl hover:border-slate-600 hover:bg-slate-800/50 transition-all group relative text-center h-full">
                <div className={`${action.bg} p-3 rounded-xl mb-2 group-hover:scale-110 transition-transform`}>
                  <SafeIcon icon={action.icon} className={`w-5 h-5 ${action.color}`} />
                </div>
                <span className="text-[10px] font-bold text-slate-400 group-hover:text-white uppercase tracking-wider">{action.title}</span>
                {action.badge > 0 && <span className="absolute top-2 right-2 bg-red-500 text-white text-[9px] font-bold h-5 w-5 rounded-full flex items-center justify-center shadow-lg border-2 border-[#0f172a]">{action.badge}</span>}
              </Link>
            ))}
          </div>
        )}

        {activeTab === 'email' && <div className="bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden h-[600px]"><EmailTemplateManager onClose={() => setActiveTab('overview')} /></div>}
      </main>
    </div>
  );
};

export default SuperAdminDashboard;